import { consume } from '@lit/context';
import { LitElement, css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { guard } from 'lit/directives/guard.js';
import { createRef, ref, type Ref } from 'lit/directives/ref.js';
import { repeat } from 'lit/directives/repeat.js';

import { CHAT_INPUT_MAX_CHARS } from '../../../../game/Chat';
import { EMOTE_NAMES } from '../../../../generated/emotes';
import { gameApiContext } from '../../../context';
import type { GameAPIType } from '../../../GameAPI';
import { scrollbarThin } from '../../../theme/scrollbar.css';
import { tokens } from '../../../theme/tokens.css';
import { uiState as uiStateProxy, type ChatMessageEntry } from '../../../UIState';
import { bindValtio } from '../../../ValtioController';
import { renderChatText } from '../chat-inline';
import { captureReadingAnchor, jumpsToLatest, restoreReadingAnchor, type ReadingAnchor } from '../chat-scroll';

/**
 * Mobile Chat — read-side pass.
 *
 * Mobile has no room (and no keyboard reach) for the desktop `game-chat`
 * panel, so chat there is a purpose-built surface rather than a responsive
 * retrofit of the 1753-line desktop component:
 *
 *  - a badge button in the top-left comms corner (unread count since last open)
 *  - a bottom-sheet drawer that opens on tap and shows the full history
 *  - a compose row in the drawer: channel chip (All/Team — hidden in FFA),
 *    16px text field (iOS auto-zooms the page on anything smaller), an emote
 *    grid, and send. Everything routes through the same Chat.ts router as
 *    desktop, so typed /commands work unchanged. A focused input raises the
 *    chatBoxOpen guard (setMobileChatComposing) so Input.ts treats keystrokes
 *    as typing, and the drawer rides above the on-screen keyboard by tracking
 *    visualViewport.
 *
 * The desktop `game-chat` still returns `nothing` on mobile; this renders only
 * on mobile. Incoming messages already populate `uiState.chat.messages` (the
 * receive path was un-gated game-side), and sends were never mobile-gated.
 *
 * Messages render through the shared `renderChatText` (same as desktop):
 * clickable truncated links, inline emote images, inline flag images.
 *
 * NOT PORTING a glanceable "peek" (auto-fading incoming lines overlaid during
 * play, kill-feed style): built and cut after on-device testing 2026-07-03 —
 * a phone HUD has no strip that fits a text feed without overlapping the
 * minimap (portrait) or game messages (landscape). The unread badge is the
 * live "something was said" signal; don't reintroduce an overlay without new
 * screen real estate.
 *
 * LANDSCAPE POLICY: read anywhere, type in portrait. The drawer, the message
 * list and the emote grid all render in landscape; only the text field (with
 * its channel toggle and send button) is withheld, because the on-screen
 * keyboard leaves about one visible line of conversation there — tested on
 * device and rejected 2026-07-04. Emotes still SEND in landscape, so it is not
 * a read-only mode. An earlier 500px device-short-side floor hid button,
 * badge and drawer instead, which put every phone in landscape — how the game
 * is actually played — behind "there's no chat on mobile"; removed 2026-09-09
 * in favour of withholding just the broken part.
 *
 * @element mobile-chat
 */
@customElement('mobile-chat')
export class MobileChat extends LitElement {
  static override styles = [
    tokens,
    scrollbarThin,
    css`
      /* Full-viewport overlay that does not capture touches itself — only the
         button, scrim, and drawer do (pointer-events: auto below). Mirrors
         touch-controls: iOS hit-testing is unreliable for position:fixed
         children of a display:contents host (the button rendered but never
         received taps), so we use the proven overlay pattern instead. The host
         fills airmash-game (which is position:relative and viewport-sized). */
      :host {
        position: absolute;
        inset: 0;
        pointer-events: none;
        font-family: var(--font-family);
        -webkit-user-select: none;
        user-select: none;
        -webkit-touch-callout: none;
        -webkit-tap-highlight-color: transparent;
      }

      /* ===== Badge button (top-left comms corner) ===== */
      .chat-btn {
        position: absolute;
        pointer-events: auto;
        /* + the status-bar inset, in step with the top bar (see airmash-game). */
        top: calc(64px + env(safe-area-inset-top, 0px));
        left: calc(12px + env(safe-area-inset-left, 0px));
        width: 44px;
        height: 44px;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: var(--color-bg-chip);
        border: none;
        border-radius: var(--radius-md);
        padding: 0;
        cursor: pointer;
        z-index: 150;
        touch-action: manipulation;
        -webkit-tap-highlight-color: transparent;
      }

      .chat-btn svg {
        width: 22px;
        height: 22px;
        fill: none;
        stroke: var(--color-text-white);
        stroke-width: 2;
        stroke-linecap: round;
        stroke-linejoin: round;
        opacity: 0.85;
      }

      .badge {
        position: absolute;
        top: -4px;
        right: -4px;
        min-width: 18px;
        height: 18px;
        box-sizing: border-box;
        padding: 0 4px;
        border-radius: var(--radius-lg);
        background-color: var(--color-team-red);
        color: var(--color-text-white);
        font-size: var(--font-size-xs);
        font-weight: bold;
        line-height: 18px;
        text-align: center;
      }

      /* ===== Bottom-sheet drawer ===== */
      /* Invisible tap-to-close layer — deliberately NO dim: with cruise keeping
         the ship flying while the drawer is open, the strip of game above the
         sheet stays fully visible. */
      .scrim {
        position: absolute;
        inset: 0;
        pointer-events: auto;
        background-color: transparent;
        /* Tap-close only — a drag here while the keyboard is open would pan
           the whole visual viewport (see .drawer-header). Blocking pans does
           not block the click. */
        touch-action: none;
        z-index: 200;
      }

      .drawer {
        position: absolute;
        pointer-events: auto;
        left: 0;
        right: 0;
        bottom: 0;
        height: min(70vh, 70dvh);
        display: flex;
        flex-direction: column;
        /* Sheet translucency (a step darker than the toolbar chips, well shy
           of the 0.9 panel black): the game stays visible through the sheet —
           you're often still flying under cruise while reading. Text carries
           --shadow-text, same recipe as the toolbars. */
        background-color: var(--color-bg-sheet);
        border-top-left-radius: var(--radius-lg);
        border-top-right-radius: var(--radius-lg);
        border-top: 2px solid var(--color-border-light);
        box-shadow: var(--shadow-panel);
        /* The drawer is full-bleed, so in landscape the notch/Dynamic Island
           overlaps its left (or right) edge — inset the content clear of it.
           The insets are 0 in portrait and on island-less screens. */
        padding-left: env(safe-area-inset-left, 0px);
        padding-right: env(safe-area-inset-right, 0px);
        padding-bottom: env(safe-area-inset-bottom, 0px);
        /* NO touch-action here: the page-pan guard lives on the individual
           non-scrolling rows (header, compose row — see .drawer-header), NOT
           the drawer root. Spec says ancestors above the nearest scroll
           container aren't consulted for a pan, so a root-level none
           shouldn't affect .messages — but if WebKit walks the whole chain it
           freezes list scrolling dead, and that bet isn't worth the 12px of
           root padding it would additionally cover. */
        z-index: 201;
        animation: slideUp 0.2s ease-out;
      }

      @keyframes slideUp {
        from {
          transform: translateY(100%);
        }
        to {
          transform: translateY(0);
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .drawer {
          animation: none;
        }
      }

      .drawer-header {
        flex: 0 0 auto;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 12px 16px;
        border-bottom: 1px solid var(--color-border-light);
        color: var(--color-text-white);
        font-weight: bold;
        /* While the keyboard is open, iOS turns any drag it can't hand to a
           scroll container into a PAGE pan (visual-viewport offset) — and the
           drawer's keyboard-tracking then counter-moves against the finger:
           inverted, stuttering, and the app's fixed layers shear apart. none
           on each non-scrolling row (here + .compose-row) kills pans at the
           source; swipe-to-close still works (our own touchstart/touchend
           listeners — touch EVENTS keep firing, only native panning stops). */
        touch-action: none;
      }

      /* A 44px target; the negative margins keep the header's height and the
         ✕ where a 32px button put them. */
      .close-btn {
        width: 44px;
        height: 44px;
        margin: -6px -6px -6px 0;
        display: flex;
        align-items: center;
        justify-content: center;
        background: transparent;
        border: none;
        cursor: pointer;
        color: var(--color-text-tertiary);
        font-size: var(--font-size-xl);
        line-height: 1;
        touch-action: manipulation;
        -webkit-tap-highlight-color: transparent;
      }

      .messages {
        flex: 1 1 auto;
        min-height: 0;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
        /* contain alone doesn't hold on iOS: the autoscroll pins this list to
           its bottom edge, and a drag at a hard edge chains to the page pan
           anyway — the touchstart edge-nudge (handleMessagesTouchStart) is
           the guard that actually keeps the gesture inside the list. */
        overscroll-behavior: contain;
        touch-action: pan-y;
        /* Reading position is held by hand (restoreReadingAnchor), the same
           way in every browser; Safari has no scroll anchoring to lean on. */
        overflow-anchor: none;
        padding: 8px 16px 12px;
        font-size: var(--font-size-13);
      }

      .empty {
        color: var(--color-text-tertiary);
        text-align: center;
        padding: 24px 0;
      }

      .line {
        margin-bottom: 6px;
        line-height: 1.4;
        text-shadow: var(--shadow-text);
        word-break: break-word;
      }

      .tag {
        display: inline-block;
        border-radius: var(--radius-sm);
        padding: 1px 5px;
        margin-right: 5px;
        font-size: var(--font-size-sm);
        font-weight: bold;
        color: var(--color-text-white);
      }
      .tag.team {
        background-color: var(--color-chat-team);
      }
      .tag.whisper {
        background-color: var(--color-chat-whisper);
      }

      /* Nick color is IDENTITY (which team is talking), not channel — the
         channel is already carried by the tag and text color. Blue/red in
         team modes, green otherwise (see nickColorClass). Exception: TEAM
         lines keep the channel purple for the whole line (the maintainer's call —
         everyone on a team line is your side, identity adds nothing). */
      .nick {
        font-weight: bold;
        color: var(--color-text-white);
        margin-right: 6px;
      }
      .nick.team {
        color: var(--color-chat-team-text);
      }
      .nick.blue {
        color: var(--color-team-blue);
      }
      .nick.red {
        color: var(--color-team-red);
      }
      .nick.green {
        color: var(--color-chat-whisper-text);
      }

      .text {
        color: var(--color-text-primary);
      }
      .text.team {
        color: var(--color-chat-team-text);
      }
      .text.whisper {
        color: var(--color-chat-whisper-text);
      }

      .say-icon {
        margin: 0 4px 0 2px;
      }

      .system {
        color: var(--color-text-tertiary);
      }

      /* Inline chat-text pieces emitted by renderChatText (shared with the
         desktop game-chat; classes styled per-component — self-contained). */
      .line .inline-emote {
        display: inline-block;
        width: 18px;
        height: 18px;
        vertical-align: middle;
        margin: 0 1px;
      }

      .line .inline-flag {
        display: inline-block;
        width: 20px;
        height: 16px;
        background-size: contain;
        background-repeat: no-repeat;
        background-position: center;
        vertical-align: middle;
        margin: 0 2px;
      }

      .line a {
        color: var(--color-link-url);
      }

      .sr-only {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
      }

      /* ===== Compose row ===== */
      .compose-row {
        flex: 0 0 auto;
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 12px;
        border-top: 1px solid var(--color-border-light);
        /* Page-pan guard — see .drawer-header. The input re-allows its own
           native touch behavior below. */
        touch-action: none;
      }

      .compose-input {
        flex: 1 1 auto;
        min-width: 0;
        height: 40px;
        box-sizing: border-box;
        padding: 0 12px;
        /* 16px is a floor, not a style choice: any smaller and iOS Safari
           auto-zooms the whole page when the field is focused. */
        font-size: 16px;
        font-family: var(--font-family);
        color: var(--color-text-white);
        background-color: var(--color-overlay-medium);
        border: none;
        border-radius: var(--radius-md);
        outline: none;
        -webkit-appearance: none;
      }

      .compose-input::placeholder {
        color: var(--color-text-tertiary);
      }

      /* Landscape stand-in for the text field. Not an <input>: focusing one
         in landscape is the thing we're avoiding, and a disabled input still
         reads as "broken text box" rather than "typing lives elsewhere". */
      .rotate-hint {
        flex: 1 1 auto;
        min-width: 0;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        padding: 0 12px;
        font-size: var(--font-size-sm);
        font-family: var(--font-family);
        color: var(--color-text-tertiary);
        background-color: var(--color-overlay-medium);
        border-radius: var(--radius-md);
        text-align: center;
      }

      .rotate-hint svg {
        flex: 0 0 auto;
        width: 16px;
        height: 16px;
        fill: none;
        stroke: currentColor;
        stroke-width: 2;
        stroke-linecap: round;
        stroke-linejoin: round;
      }

      /* Re-allow native touch behavior inside the input (caret drag,
         long-press selection, panning long text) under .compose-row's
         touch-action: none, and selection under the host's. */
      .compose-input {
        touch-action: auto;
        -webkit-user-select: text;
        user-select: text;
      }

      /* Channel chip + icon buttons share the 40px chip look. All compose
         buttons act on touchstart with preventDefault — that both keeps the
         focus (and so the keyboard) on the input when tapped mid-typing and
         suppresses the iOS magnifier; the synthetic click never fires. The
         @click bindings only serve the desktop dev preview. */
      .chan,
      .icon-btn {
        flex: 0 0 auto;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: var(--color-overlay-medium);
        border: none;
        border-radius: var(--radius-md);
        color: var(--color-text-white);
        cursor: pointer;
        touch-action: manipulation;
        -webkit-tap-highlight-color: transparent;
      }

      .chan:active,
      .icon-btn:active,
      .emote:active {
        background-color: var(--color-overlay-strong);
      }

      .chan {
        min-width: 52px;
        padding: 0 10px;
        font-family: var(--font-family);
        font-size: var(--font-size-13);
        font-weight: bold;
      }

      .chan.team {
        background-color: var(--color-chat-team);
      }

      .icon-btn {
        width: 40px;
        font-size: var(--font-size-xl);
        line-height: 1;
      }

      .icon-btn svg {
        width: 20px;
        height: 20px;
        fill: none;
        stroke: var(--color-text-white);
        stroke-width: 2;
        stroke-linecap: round;
        stroke-linejoin: round;
      }

      .icon-btn.active {
        background-color: var(--color-overlay-strong);
      }

      /* ===== Emote grid (toggled by the 😀 button) ===== */
      /* Takes the message list's place while open — a full-panel grid shows
         dozens of emotes at once instead of a narrow strip. Sending closes
         the whole drawer, so there's no reading to preserve underneath.
         Taps are decided on touchend (see handleEmoteTouchEnd), never by
         preventing touchstart, so the grid still scrolls. */
      .emote-grid {
        flex: 1 1 auto;
        min-height: 0;
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(38px, 1fr));
        grid-auto-rows: 38px;
        align-content: start;
        justify-items: center;
        gap: 4px 6px;
        padding: 8px 12px 12px;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
        overscroll-behavior: contain;
        touch-action: pan-y;
      }

      .emote {
        flex: 0 0 auto;
        width: 38px;
        height: 38px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: transparent;
        border: none;
        border-radius: var(--radius-sm);
        padding: 0;
        cursor: pointer;
        touch-action: manipulation;
        -webkit-tap-highlight-color: transparent;
      }

      .emote img {
        width: 30px;
        height: 30px;
      }
    `,
  ];

  private state = bindValtio(this, uiStateProxy);

  @consume({ context: gameApiContext })
  private gameApi!: GameAPIType;

  private messagesRef: Ref<HTMLElement> = createRef();

  /** Highest message id the user has seen; messages after it are "unread".
   *  Component-local (the drawer's open/closed state is its own thing, distinct
   *  from the desktop minimize/unread semantics). */
  private lastSeenId = 0;

  // Autoscroll bookkeeping (non-reactive).
  private prevOpen = false;
  private prevKeyboardInset = 0;
  private prevDeviceLandscape: boolean | null = null;
  private prevEmotesOpen = false;

  // A new line's scroll decision: made in willUpdate before it renders,
  // applied in updated() (see chat-scroll).
  private prevNewestId = 0;
  private scrollToLatest = false;
  private readingAnchor: ReadingAnchor | null = null;

  private composeInputRef: Ref<HTMLInputElement> = createRef();

  /** Compose target. Forced to All in FFA (the chip is hidden there). */
  @state() private composeChannel: 'all' | 'team' = 'all';

  /** Whether the tap-to-send emote strip is expanded above the compose row. */
  @state() private emotesOpen = false;

  /** Pixels the on-screen keyboard overlaps the drawer's containing block
   *  (0 = keyboard closed). Lifts the drawer so the compose row and the
   *  newest messages stay visible above the keyboard.
   *
   *  Measured as host rect bottom minus visual-viewport bottom — NOT from
   *  window.innerHeight. iOS 26 shrinks innerHeight for the keyboard (late,
   *  via a window resize) while the layout box keeps its height, so the old
   *  innerHeight math lifted the drawer on the first visualViewport event and
   *  then snapped it back under the keyboard when the shrunken innerHeight
   *  arrived. The rect-based form is correct in both worlds: if the layout
   *  truly shrinks the overlap is 0 (bottom: 0 already sits above the
   *  keyboard); if only the visual viewport shrinks it's the real overlap. */
  @state() private keyboardInset = 0;

  /** Drawer height while the keyboard is open — fills the visible strip above
   *  it (minus a top reveal so the scrim stays tappable to dismiss). */
  @state() private keyboardDrawerHeight = 0;

  /** True when the DEVICE is physically landscape. Deliberately NOT
   *  uiState.viewport.orientation (visual-viewport aspect): the iOS keyboard
   *  can squish the visual viewport wider-than-tall mid-compose — switching
   *  to the emoji keyboard did exactly that, and the fake "rotation" blurred
   *  the input and withheld the text field mid-message. Screen
   *  metrics are keyboard-immune; renders are still triggered by the viewport
   *  state changes, so real rotations re-evaluate this. (Screen Orientation
   *  API: Safari 16.4+, well inside our support floor.) */
  private get deviceLandscape(): boolean {
    return window.screen.orientation.type.startsWith('landscape');
  }

  private get unreadCount(): number {
    const messages = this.state.chat.messages;
    let count = 0;
    for (let i = messages.length - 1; i >= 0; i--) {
      const message = messages[i];
      if (!message || message.id <= this.lastSeenId) break;
      count++;
    }
    return count;
  }

  private markSeen(): void {
    this.lastSeenId = this.state.chat.messages.at(-1)?.id ?? 0;
  }

  override firstUpdated(): void {
    // Start caught up so a pre-existing backlog doesn't flash a big badge the
    // first time the button appears.
    this.markSeen();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.detachViewportListener();
    // If the component unmounts mid-compose, the input's blur may never fire —
    // don't leave the key-suppression flag stuck on.
    this.gameApi?.setMobileChatComposing(false);
  }

  /** Tracks the on-screen keyboard while the drawer is open — see the
   *  keyboardInset doc for why the overlap is measured from the host's own
   *  rect. offsetTop covers Safari panning the visual viewport to reveal a
   *  focused input. */
  private readonly handleViewportChange = (): void => {
    const viewport = window.visualViewport;
    if (!viewport) return;
    // iOS sometimes scrolls the LAYOUT viewport to "reveal" the focused input
    // (we lift the drawer above the keyboard ourselves, so the right amount is
    // zero) — and while it's nonzero, every fixed layer in the app is
    // displaced. Snap it straight back.
    if (window.scrollX !== 0 || window.scrollY !== 0) {
      window.scrollTo(0, 0);
    }
    // Keyboard top and the drawer's containing block bottom, both in client
    // coordinates — the overlap between them is the lift.
    const keyboardTop = viewport.offsetTop + viewport.height;
    const hostBottom = this.getBoundingClientRect().bottom;
    this.keyboardInset = Math.max(0, Math.round(hostBottom - keyboardTop));
    this.keyboardDrawerHeight = Math.max(160, Math.round(viewport.height - 56));
  };

  private attachViewportListener(): void {
    window.visualViewport?.addEventListener('resize', this.handleViewportChange);
    window.visualViewport?.addEventListener('scroll', this.handleViewportChange);
    // Rotation can land as a window resize before (or without) a
    // visualViewport event on some browsers — recompute on both.
    window.addEventListener('resize', this.handleViewportChange);
  }

  private detachViewportListener(): void {
    window.visualViewport?.removeEventListener('resize', this.handleViewportChange);
    window.visualViewport?.removeEventListener('scroll', this.handleViewportChange);
    window.removeEventListener('resize', this.handleViewportChange);
    this.keyboardInset = 0;
  }

  /** Reactive close-edge resets. These are @state sets, so they belong here —
   *  setting reactive state from updated() schedules a follow-on update and
   *  trips Lit's dev "scheduled an update after an update completed" warning;
   *  in willUpdate the changes fold into the current render. prevOpen still
   *  holds last cycle's value here (updated() advances it afterwards). */
  protected override willUpdate(): void {
    if (!this.state.chat.mobileDrawerOpen && this.prevOpen) {
      this.emotesOpen = false;
      // The channel is per-conversation, not sticky: a Team selection left
      // behind would silently reroute your next session's messages.
      this.composeChannel = 'all';
      this.keyboardInset = 0;
      this.keyboardDrawerHeight = 0;
    }
    this.decideScroll();
  }

  /** Decides how the list scrolls for a new line, before it renders (see
   *  chat-scroll). This re-renders on every uiState change, so it only
   *  measures when the newest line changed. Near the newest line, or for a
   *  line that jumps there, it follows; while reading back it holds a visible
   *  line in place. */
  private decideScroll(): void {
    this.scrollToLatest = false;
    this.readingAnchor = null;
    const newest = this.state.chat.messages.at(-1);
    const newestId = newest?.id ?? 0;
    if (newestId === this.prevNewestId) return;
    this.prevNewestId = newestId;
    // Closed, or the emote grid has the list's place: opening the drawer and
    // closing the grid both snap to the latest anyway.
    const container = this.messagesRef.value;
    if (!newest || !this.state.chat.mobileDrawerOpen || !container?.isConnected) return;
    // Some slack, so a flick that stops just short of the end still follows.
    const nearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 40;
    if (nearBottom || jumpsToLatest(newest)) {
      this.scrollToLatest = true;
    } else {
      this.readingAnchor = captureReadingAnchor(container);
    }
  }

  override updated(): void {
    const open = this.state.chat.mobileDrawerOpen;
    const container = this.messagesRef.value;

    // Panels own the screen: opening any panel — the menu, or its flag page
    // via a typed /flag in compose — closes the drawer rather than layering
    // with it.
    if (open && (this.state.activePanel !== null || this.state.openTopBarPanel !== null)) {
      this.gameApi.closeMobileChat();
    }

    if (open && !this.prevOpen) {
      this.attachViewportListener();
    } else if (!open && this.prevOpen) {
      this.detachViewportListener();
      // Belt for a scrim-close mid-compose: unmounting the focused input may
      // not fire blur on iOS, which would leave key suppression stuck on.
      // (The reactive close-edge resets live in willUpdate.)
      this.gameApi.setMobileChatComposing(false);
    }

    if (open && container) {
      const justOpened = !this.prevOpen;
      // The keyboard or a rotation resizing the drawer changes the scroll
      // geometry — snap back to the latest, which is also where composing
      // wants your eyes.
      const keyboardMoved = this.keyboardInset !== this.prevKeyboardInset;
      // Device rotation, not viewport aspect — see deviceLandscape.
      const rotated = this.prevDeviceLandscape !== null && this.deviceLandscape !== this.prevDeviceLandscape;
      // Rotating with the keyboard up lands in a half-broken intermediate
      // (iOS re-lays the keyboard out mid-rotation and the two orientations
      // disagree about the viewport) — dismiss the keyboard instead and let
      // the drawer settle into the clean closed-keyboard layout. Blurring an
      // unfocused input is a no-op, so no focus check is needed.
      if (rotated) {
        this.composeInputRef.value?.blur();
      }
      // Closing the emote grid remounts the message list at scrollTop 0 —
      // snap it back to the latest.
      const emotesClosed = this.prevEmotesOpen && !this.emotesOpen;
      if (justOpened || keyboardMoved || rotated || emotesClosed || this.scrollToLatest) {
        container.scrollTop = container.scrollHeight;
      } else if (this.readingAnchor) {
        restoreReadingAnchor(container, this.readingAnchor);
      }
      // Reading live keeps you caught up.
      this.markSeen();
    }

    this.prevOpen = open;
    this.prevKeyboardInset = this.keyboardInset;
    this.prevDeviceLandscape = this.deviceLandscape;
    this.prevEmotesOpen = this.emotesOpen;
  }

  /** Edge guards shared by the two scroll containers (message list + emote
   *  grid). The autoscroll pins the list to its bottom edge, and on iOS a
   *  drag on a scroll container sitting AT a hard edge chains straight to the
   *  page pan that `.drawer-header`'s touch-action comment describes —
   *  overscroll-behavior does not stop that while the keyboard is open.
   *  Nudging 1px off the edges on touchstart keeps the gesture inside the
   *  container (a container mid-scroll never chains); when there's nothing to
   *  scroll at all, touchmove is swallowed outright. */
  private readonly handleScrollerTouchStart = (e: TouchEvent) => {
    const el = e.currentTarget;
    if (!(el instanceof HTMLElement)) return;
    const maxScroll = el.scrollHeight - el.clientHeight;
    if (maxScroll <= 0) return; // nothing to scroll — the touchmove guard covers it
    if (el.scrollTop <= 0) {
      el.scrollTop = 1;
    } else if (el.scrollTop >= maxScroll - 1) {
      el.scrollTop = maxScroll - 1;
    }
  };

  private readonly handleScrollerTouchMove = (e: TouchEvent) => {
    const el = e.currentTarget;
    if (el instanceof HTMLElement && el.scrollHeight <= el.clientHeight) {
      e.preventDefault();
    }
  };

  private readonly openDrawer = () => {
    this.gameApi.playClick();
    this.markSeen();
    this.gameApi.openMobileChat();
  };

  /** Silent: the scrim, the swipe and an emote send close it this way too. */
  private readonly closeDrawer = () => {
    this.markSeen();
    this.gameApi.closeMobileChat();
  };

  private readonly handleCloseTap = () => {
    this.gameApi.playClick();
    this.closeDrawer();
  };

  // ===== Compose =====

  private readonly handleComposeFocus = () => {
    this.gameApi.setMobileChatComposing(true);
  };

  private readonly handleComposeBlur = () => {
    this.gameApi.setMobileChatComposing(false);
  };

  /** Enter sends (the iOS keyboard's Send key arrives as Enter), Escape just
   *  dismisses the keyboard. Both stop propagation so the window-level game
   *  handlers never see them; everything else is already swallowed game-side
   *  by the chatBoxOpen guard while composing. */
  private readonly handleComposeKeydown = (e: KeyboardEvent) => {
    // Hardware-keyboard IMEs (e.g. Japanese on iPad) commit a composition
    // with Enter; that keydown must insert, not send. Chromium sets
    // isComposing, Safari fires it after compositionend with keyCode 229.
    if (e.isComposing || e.keyCode === 229) {
      e.stopPropagation();
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      this.sendCompose();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      this.composeInputRef.value?.blur();
    }
  };

  private sendCompose(): void {
    const input = this.composeInputRef.value;
    if (!input) return;
    const text = input.value.trim();
    if (!text) return;
    // A typed /command always goes raw (the power path — same router as
    // desktop); otherwise the channel chip picks public vs team chat.
    const useTeam = this.composeChannel === 'team' && this.state.gameType !== 'ffa';
    this.gameApi.submitChatMessage(useTeam && !text.startsWith('/') ? `/t ${text}` : text);
    input.value = '';
    // Focus stays put (compose buttons preventDefault their touchstart), so
    // rapid follow-up messages don't re-summon the keyboard.
  }

  private readonly handleChannelToggle = (e: Event) => {
    e.preventDefault();
    this.gameApi.playClick();
    this.composeChannel = this.composeChannel === 'all' ? 'team' : 'all';
  };

  private readonly handleEmotesToggle = (e: Event) => {
    e.preventDefault();
    this.gameApi.playClick();
    this.emotesOpen = !this.emotesOpen;
  };

  /** Tap-vs-scroll discrimination for the emote strip. The compose buttons'
   *  preventDefault-on-touchstart trick would kill the strip's pan gesture
   *  outright (preventing touchstart prevents scrolling from ever starting),
   *  so emotes decide on touchEND instead: a release within 10px of its start
   *  is a tap — send, and preventDefault to suppress the synthetic click that
   *  follows (the tap closes the drawer, so an unsuppressed click would land
   *  on whatever re-renders underneath — the reopened chat button). Larger
   *  travel was a scroll. */
  private emoteTouchOrigin: { x: number; y: number } | null = null;

  private readonly handleEmoteTouchStart = (e: TouchEvent) => {
    const touch = e.touches[0];
    this.emoteTouchOrigin = touch ? { x: touch.clientX, y: touch.clientY } : null;
  };

  private readonly handleEmoteTouchEnd = (e: TouchEvent) => {
    const origin = this.emoteTouchOrigin;
    this.emoteTouchOrigin = null;
    const touch = e.changedTouches[0];
    if (!origin || !touch) return;
    if (Math.hypot(touch.clientX - origin.x, touch.clientY - origin.y) > 10) return;
    e.preventDefault();
    this.sendEmoteFrom(e.currentTarget);
  };

  /** Desktop dev-preview path — on touch the synthetic click is suppressed. */
  private readonly handleEmoteClick = (e: Event) => {
    this.sendEmoteFrom(e.currentTarget);
  };

  /** Same wire format as the emotes panel (`/name` → SAY `:name:`), and the
   *  same close-on-send: an emote renders as a SAY bubble above your plane in
   *  the game world, which the open drawer would hide for its whole few-second
   *  life — it looked like the tap did nothing. Closing immediately shows it.
   *  (The server's echo also appends a `say` line to the feed as a record.) */
  private sendEmoteFrom(target: EventTarget | null): void {
    if (!(target instanceof HTMLElement)) return;
    const name = target.dataset['emote'];
    if (name) {
      this.gameApi.playClick();
      this.gameApi.submitChatMessage('/' + name);
      this.closeDrawer();
    }
  }

  /** Swipe-down on the header dismisses the sheet — the gesture everyone
   *  tries. Detection-only (no drag-follow visuals): a downward travel past
   *  the threshold on release closes; a tap travels nowhere, so the ✕ button
   *  is unaffected. */
  private headerTouchStartY: number | null = null;

  private readonly handleHeaderTouchStart = (e: TouchEvent) => {
    this.headerTouchStartY = e.touches[0]?.clientY ?? null;
  };

  private readonly handleHeaderTouchEnd = (e: TouchEvent) => {
    const startY = this.headerTouchStartY;
    this.headerTouchStartY = null;
    const touch = e.changedTouches[0];
    if (startY == null || !touch) return;
    if (touch.clientY - startY > 60) {
      this.closeDrawer();
    }
  };

  override render() {
    if (!this.state.isMobile) return nothing;

    const open = this.state.chat.mobileDrawerOpen;
    // Hide the button only while a full menu/panel owns the screen. Unlike the
    // touch controls, we deliberately KEEP it when dead/spectating — being dead
    // is prime chat time (idle, waiting to respawn, coordinating).
    const menuOpen = this.state.activePanel !== null || this.state.openTopBarPanel !== null;

    return html` ${!open && !menuOpen ? this.renderButton() : nothing} ${open ? this.renderDrawer() : nothing} `;
  }

  private renderButton() {
    const unread = this.unreadCount;
    return html`
      <button class="chat-btn" @click=${this.openDrawer} aria-label="Chat">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" /></svg>
        ${unread > 0 ? html`<span class="badge">${unread > 9 ? '9+' : unread}</span>` : nothing}
      </button>
    `;
  }

  private renderDrawer() {
    const messages = this.state.chat.messages;
    const teamAvailable = this.state.gameType !== 'ffa';
    const channel = teamAvailable ? this.composeChannel : 'all';
    // Typing is portrait-only. In landscape the on-screen keyboard leaves
    // roughly one visible line of conversation above it — tested on device and
    // rejected 2026-07-04. Reading and emotes need no keyboard, so they stay;
    // only the text field is withheld, with a nudge toward the orientation
    // where composing actually works. (Fullscreen no longer locks orientation,
    // so this nudge is always followable — see Graphics.toggleFullscreen.)
    const canType = !this.deviceLandscape;
    // Keyboard open: lift the drawer above it and fill the visible strip.
    const drawerStyle =
      this.keyboardInset > 0 ? `bottom: ${this.keyboardInset}px; height: ${this.keyboardDrawerHeight}px;` : '';

    return html`
      <div class="scrim" @click=${this.closeDrawer}></div>
      <div class="drawer" style=${drawerStyle}>
        <div class="drawer-header" @touchstart=${this.handleHeaderTouchStart} @touchend=${this.handleHeaderTouchEnd}>
          <span>Chat</span>
          <button class="close-btn" @click=${this.handleCloseTap} aria-label="Close chat">✕</button>
        </div>
        ${this.emotesOpen ? this.renderEmoteGrid() : this.renderMessages(messages)}
        <div class="compose-row">
          ${
            canType && teamAvailable
              ? html`
                  <button
                    class="chan ${channel === 'team' ? 'team' : ''}"
                    @touchstart=${this.handleChannelToggle}
                    @click=${this.handleChannelToggle}
                    aria-label="Sending to ${channel === 'team' ? 'team' : 'everyone'} — tap to switch"
                  >
                    ${channel === 'team' ? 'Team' : 'All'}
                  </button>
                `
              : nothing
          }
          ${
            canType
              ? html`
                  <input
                    class="compose-input"
                    type="text"
                    maxlength=${CHAT_INPUT_MAX_CHARS}
                    autocomplete="off"
                    enterkeyhint="send"
                    placeholder=${channel === 'team' ? 'Message your team' : 'Message everyone'}
                    ${ref(this.composeInputRef)}
                    @focus=${this.handleComposeFocus}
                    @blur=${this.handleComposeBlur}
                    @keydown=${this.handleComposeKeydown}
                  />
                `
              : html`
                  <div class="rotate-hint">
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M21 12a9 9 0 1 1-2.6-6.4" />
                      <path d="M21 3v5h-5" />
                    </svg>
                    <span>Rotate to type</span>
                  </div>
                `
          }
          <button
            class="icon-btn ${this.emotesOpen ? 'active' : ''}"
            @touchstart=${this.handleEmotesToggle}
            @click=${this.handleEmotesToggle}
            aria-label="Emotes"
          >
            😀
          </button>
          ${
            canType
              ? html`
                  <button
                    class="icon-btn"
                    @touchstart=${this.handleSendTap}
                    @click=${this.handleSendTap}
                    aria-label="Send"
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path
                        d="M3.714 3.048a.498.498 0 0 0-.683.627l2.843 7.627a2 2 0 0 1 0 1.396l-2.842 7.627a.498.498 0 0 0 .682.627l18-8.5a.5.5 0 0 0 0-.904z"
                      />
                      <path d="M6 12h16" />
                    </svg>
                  </button>
                `
              : nothing
          }
        </div>
      </div>
    `;
  }

  private readonly handleSendTap = (e: Event) => {
    e.preventDefault();
    this.gameApi.playClick();
    this.sendCompose();
  };

  private renderMessages(messages: ChatMessageEntry[]) {
    // Read once for the whole list rather than once per line inside the repeat
    const gameType = this.state.gameType;
    return html`
      <div
        class="messages scrollbar-thin"
        ${ref(this.messagesRef)}
        @touchstart=${this.handleScrollerTouchStart}
        @touchmove=${this.handleScrollerTouchMove}
      >
        ${
          messages.length === 0
            ? html`<div class="empty">No messages yet</div>`
            : repeat(
                messages,
                (msg) => msg.id,
                // A line never changes once sent, so it's only rebuilt when the
                // game type changes (CTF nick colours), as in game-chat.
                (msg) => guard([msg, gameType], () => this.renderMessage(msg)),
              )
        }
      </div>
    `;
  }

  private renderEmoteGrid() {
    return html`
      <div
        class="emote-grid scrollbar-thin"
        @touchstart=${this.handleScrollerTouchStart}
        @touchmove=${this.handleScrollerTouchMove}
      >
        ${EMOTE_NAMES.map(
          (name) => html`
            <button
              class="emote"
              data-emote=${name}
              @touchstart=${this.handleEmoteTouchStart}
              @touchend=${this.handleEmoteTouchEnd}
              @click=${this.handleEmoteClick}
              aria-label="Send ${name} emote"
              title="/${name}"
            >
              <img src="/vanilla/ui/emotes/emote_${name}.png" alt=${name} loading="lazy" />
            </button>
          `,
        )}
      </div>
    `;
  }

  /** Identity color class for a player name: team blue/red in CTF, green
   *  everywhere else. FFA/BTR "teams" are per-player values, so a team of
   *  1 or 2 there is a coincidence of join order, not a side — never
   *  colorize by it outside a real team mode. TEAM lines don't use this —
   *  they keep the channel purple (see the .nick CSS note). */
  private nickColorClass(player: ChatMessageEntry['player']): string {
    if (player && this.state.gameType === 'ctf') {
      if (player.team === 1) return 'blue';
      if (player.team === 2) return 'red';
    }
    return 'green';
  }

  private renderMessage(msg: ChatMessageEntry) {
    const name = msg.player?.name ?? '';
    const nick = `nick ${this.nickColorClass(msg.player)}`;
    switch (msg.type) {
      case 'team':
        return html`<div class="line">
          <span class="tag team">TEAM</span><span class="nick team">${name}</span
          ><span class="text team">${renderChatText(msg.text)}</span>
        </div>`;
      case 'whisper-to':
        return html`<div class="line">
          <span class="tag whisper">TO</span><span class=${nick}>${name}</span
          ><span class="text whisper">${renderChatText(msg.text)}</span>
        </div>`;
      case 'whisper-from':
        return html`<div class="line">
          <span class="tag whisper">FROM</span><span class=${nick}>${name}</span
          ><span class="text whisper">${renderChatText(msg.text)}</span>
        </div>`;
      case 'say':
        return html`<div class="line">
          <span class=${nick}>${name}</span><span class="say-icon">💬</span
          ><span class="text">${renderChatText(msg.text)}</span>
        </div>`;
      case 'system':
        return html`<div class="line system">${msg.isWarning ? '⚠ ' : ''}${renderChatText(msg.text)}</div>`;
      case 'public':
      default:
        return html`<div class="line">
          <span class=${nick}>${name}</span><span class="text">${renderChatText(msg.text)}</span>
        </div>`;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mobile-chat': MobileChat;
  }
}
